"use client";

import React, { useState } from 'react';
import { Car, Phone, ArrowRight, ArrowLeft, CheckCircle2, Edit2, Loader2, Shield } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { normalizeFuelType } from '@/utils/utils';
import { createStatelessSessionAction } from '@/actions/session-actions';
import { getVehicleInfoAction } from '@/actions/vehicle-actions';
import { sendOtpAction, verifyOtpAction } from '@/actions/sms-actions';
import { useOfferStore } from '@/store/useOfferStore';
import Dialog from '@/components/common/Dialog';

export default function Page() {
    const router = useRouter();

    // ZUSTAND STORE'DAN FONKSİYONU ÇEK
    const setOfferData = useOfferStore((state) => state.setOfferData);

    const [step, setStep] = useState(1);
    const [hasPlate, setHasPlate] = useState(true);
    const [loading, setLoading] = useState(false);
    const [apiLoading, setApiLoading] = useState(false);
    const [customError, setCustomError] = useState<string | null>(null);
    const [editMode, setEditMode] = useState({
        personal: false,
        vehicle: false
    });

    const [verificationCode, setVerificationCode] = useState('');
    const [tcOrTaxNo, setTcOrTaxNo] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [plateNo, setPlateNo] = useState('');
    const [registrationNo, setRegistrationNo] = useState('');
    const [plateCity, setPlateCity] = useState('');

    const [basicInfo, setBasicInfo] = useState({
        plate: plateNo ? plateNo : "",
        licenseSerialNumber: registrationNo ? registrationNo : "",
    });

    const [vehicleTechnicalInfo, setVehicleTechnicalInfo] = useState({
        vehicleKindCode: null as string | null,
        vehicleKindName: null as string | null,
        usage: null as string | null,
        vehicleBrandCode: null as string | null,
        vehicleBrandName: null as string | null,
        vehicleModelCode: null as string | null,
        vehicleModelName: null as string | null,
        modelYear: "",
        chassisNumber: null as string | null,
        engineNumber: null as string | null,
        fuelType: null as string | null,
    });

    const [vehicleStatusInfo, setVehicleStatusInfo] = useState<any>({});
    const [trafficInsurance, setTrafficInsurance] = useState<any>({});
    const [ownerInfo, setOwnerInfo] = useState<any>({});
    const [personalInfo, setPersonalInfo] = useState({
        name: "",
        surname: "",
        city: "",
        district: "",
        job: "",
        phoneNo: "",
        tcOrTaxNo: tcOrTaxNo,
        birthDate: birthDate,
    });

    // PARAMETRELERE TİPLER EKLENDİ
    const fetchVehicleInfo = async (licenseSerialNumber: string, taxIdentityNumber: string, plate: string) => {
        setLoading(true);
        setCustomError(null);

        try {
            const result = await getVehicleInfoAction(licenseSerialNumber, taxIdentityNumber, plate);

            if (!result.success) {
                setCustomError(result.error || "Bir hata oluştu");
                return null;
            }

            // Verileri state'e kaydet
            const data = result.data;
            const [name, ...surnameParts] = (data.ownerInfo?.vehicleOwner || "").split(" ");
            const surname = surnameParts.join(" ");
            const ownerTcOrTaxNo = data.ownerInfo?.taxIdentityNumber || "";
            const ownerBirthDate = data.ownerInfo?.dateOfBirth || "";

            let city = "";
            let district = "";
            if (data.contactInfo?.cityDistrictName) {
                const parts = data.contactInfo.cityDistrictName.split(" - ");
                city = parts[0] || "";
                district = parts[1] || "";
            }

            setPersonalInfo({
                ...personalInfo,
                name: name || "",
                surname: surname || "",
                city: city || "",
                district: district || "",
                tcOrTaxNo: ownerTcOrTaxNo,
                birthDate: ownerBirthDate,
            });

            setVehicleTechnicalInfo(data.vehicleTechnicalInfo || {});
            setVehicleStatusInfo(data.vehicleStatusInfo || {});
            setBasicInfo(data.basicInfo || {});
            setTrafficInsurance(data.insuranceInfo?.trafficInsurance || {});
            setOwnerInfo(data.ownerInfo || {});

            return result.data;

        } catch (error) {
            setCustomError("Araç bilgisi alınamadı. Lütfen bilgilerinizi manuel olarak giriniz.");
            return null;
        } finally {
            setLoading(false);
        }
    };

    const handleNextStep = async () => {
        if (step === 1) {
            if (!plateNo || !registrationNo) {
                setCustomError('Lütfen araç bilgilerini doldurun');
                return;
            }
            setStep(2);
        }
        else if (step === 2) {
            if (!tcOrTaxNo || !birthDate || !phoneNumber) {
                setCustomError('Lütfen tüm alanları doldurun');
                return;
            }

            setLoading(true);
            try {
                const result = await sendOtpAction(phoneNumber);

                if (result.success) {
                    setStep(3);
                } else {
                    setCustomError(result.message || "SMS gönderilemedi");
                }
            } catch (error) {
                setCustomError("Teknik bir hata oluştu");
            } finally {
                setLoading(false);
            }
        }
        else if (step === 3) {
            if (verificationCode.length !== 6) {
                setCustomError('Lütfen 6 haneli kodu giriniz');
                return;
            }

            setLoading(true);
            try {
                const result = await verifyOtpAction(phoneNumber, verificationCode);

                if (result.success) {
                    const vehicleData = await fetchVehicleInfo(registrationNo, tcOrTaxNo, plateNo);
                    if (vehicleData) {
                        setStep(4);
                    }
                } else {
                    setCustomError(result.message || "Kod hatalı");
                }
            } catch (error) {
                setCustomError("Doğrulama sırasında hata oluştu");
            } finally {
                setLoading(false);
            }
        }
        else if (step === 4) {
            await saveSession();
        }
    };

    const handlePrevStep = () => {
        if (step > 1) {
            setStep(step - 1);
        }
    };

    // --- YENİ ZUSTAND VE ŞİFRELİ TOKEN SİSTEMİ ---
    const saveSession = async () => {
        setApiLoading(true);
        try {
            const sessionData = {
                personalInfo,
                vehicleTechnicalInfo,
                basicInfo,
                vehicleStatusInfo,
                trafficInsurance,
                ownerInfo,
                type: 'Kasko' // Tipi KASKO olarak belirliyoruz
            };

            // 1. Veritabanına YAZMADAN şifreli token oluştur
            const result = await createStatelessSessionAction(sessionData);

            if (!result.success || !result.token) {
                throw new Error(result.error || "Token oluşturulamadı");
            }

            // 2. Teklifler sayfasında ekranda güvenle göstermek için özet bilgiler
            const safeDisplayData = {
                type: 'Kasko',
                name: personalInfo.name,
                surname: personalInfo.surname,
                plate: basicInfo.plate,
                vehicle: `${vehicleTechnicalInfo.vehicleBrandName || ''} ${vehicleTechnicalInfo.vehicleModelName || ''}`.trim(),
            };

            // 3. Zustand ile RAM'e kaydet (F5 atılınca silinir)
            setOfferData(result.token, safeDisplayData);

            // 4. Teklifler sayfasına yönlendir
            router.push("/teklif-al/teklifler");

        } catch (error: any) {
            alert("Teklifler sayfasına yönlenirken hata oluştu: " + error.message);
        } finally {
            setApiLoading(false);
        }
    };

    // Şehirler listesi
    const cities = [
        "Adana", "Adıyaman", "Afyonkarahisar", "Ağrı", "Amasya", "Ankara", "Antalya",
        "Artvin", "Aydın", "Balıkesir", "Bilecik", "Bingöl", "Bitlis", "Bolu", "Burdur",
        "Bursa", "Çanakkale", "Çankırı", "Çorum", "Denizli", "Diyarbakır", "Edirne",
        "Elazığ", "Erzincan", "Erzurum", "Eskişehir", "Gaziantep", "Giresun", "Gümüşhane",
        "Hakkari", "Hatay", "Isparta", "Mersin", "İstanbul", "İzmir", "Kars", "Kastamonu",
        "Kayseri", "Kırklareli", "Kırşehir", "Kocaeli", "Konya", "Kütahya", "Malatya",
        "Manisa", "Kahramanmaraş", "Mardin", "Muğla", "Muş", "Nevşehir", "Niğde", "Ordu",
        "Rize", "Sakarya", "Samsun", "Siirt", "Sinop", "Sivas", "Tekirdağ", "Tokat",
        "Trabzon", "Tunceli", "Şanlıurfa", "Uşak", "Van", "Yozgat", "Zonguldak", "Aksaray",
        "Bayburt", "Karaman", "Kırıkkale", "Batman", "Şırnak", "Bartın", "Ardahan", "Iğdır",
        "Yalova", "Karabük", "Kilis", "Osmaniye", "Düzce"
    ];

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('tr-TR');
    };

    return (
        <section className="relative min-h-screen flex items-center justify-center bg-linear-to-br from-blue-50 via-white to-blue-100 overflow-hidden pt-24 pb-16 px-4">
            {/* Dekoratif arkaplan elementleri */}
            <div className="absolute top-0 left-0 w-full h-1/2 bg-linear-to-b from-blue-100/50 to-transparent"></div>
            <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-600/15 rounded-full blur-3xl"></div>

            {/* Grid nokta deseni */}
            <div className="absolute inset-0 bg-[radial-gradient(#d1e0f8_1px,transparent_1px)] [bg-size:24px_24px]"></div>

            <div className="container relative z-10 mx-auto max-w-7xl flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">

                {/* Sol Taraf - Açıklama */}
                <div className="w-full lg:w-1/2 text-center lg:text-left">
                    <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
                        <Shield className="w-4 h-4 mr-2" /> Kasko Sigortası
                    </div>

                    <h1 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-6">
                        Kaskonuzu <span className="text-blue-600">Hemen Alın</span>
                    </h1>
                    <p className="text-lg text-gray-600 mb-8">
                        Aracınızı kapsamlı risklere karşı güvence altına alın.
                        En uygun fiyat garantisi ve anında poliçe teslimi.
                    </p>

                    {/* İlerleme Çubuğu */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-600">Adım {step}/4</span>
                            <span className="text-sm text-gray-600">%{step * 25}</span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${step * 25}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* Adım Açıklamaları */}
                    <div className="space-y-4">
                        <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                                {step > 3 ? <CheckCircle2 className="w-4 h-4" /> : '1'}
                            </div>
                            <span className="font-medium">Araç Bilgileri</span>
                        </div>
                        <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                                {step > 1 ? <CheckCircle2 className="w-4 h-4" /> : '2'}
                            </div>
                            <span className="font-medium">Kişisel Bilgiler</span>
                        </div>
                        <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                                {step > 2 ? <CheckCircle2 className="w-4 h-4" /> : '3'}
                            </div>
                            <span className="font-medium">Telefon Doğrulama</span>
                        </div>
                        <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                                4
                            </div>
                            <span className="font-medium">Özet & Teklifler</span>
                        </div>
                    </div>
                </div>

                {/* Sağ Taraf - Form */}
                <div className="w-full lg:w-1/2 max-w-md">
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-blue-100/50 p-8">

                        {/* Adım 1: Araç Bilgileri */}
                        {step === 1 && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-center mb-2">
                                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                                        <Car className="w-5 h-5 text-white" />
                                    </div>
                                    <h2 className="text-2xl font-semibold text-gray-800">
                                        Araç Bilgileri
                                    </h2>
                                </div>
                                <p className="text-gray-600 text-center mb-6">
                                    Lütfen araç bilgilerinizi giriniz
                                </p>

                                {/* Plaka Durumu Seçimi */}
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                        Plaka Durumu
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={() => setHasPlate(true)}
                                            className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${hasPlate
                                                ? 'border-blue-600 bg-blue-50 text-blue-700'
                                                : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                                                }`}
                                        >
                                            Plakam Var
                                        </button>
                                        <button
                                            onClick={() => setHasPlate(false)}
                                            className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${!hasPlate
                                                ? 'border-blue-600 bg-blue-50 text-blue-700'
                                                : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                                                }`}
                                        >
                                            Plakam Yok
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {/* Plaka Input */}
                                    {hasPlate ? (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2"> Araç Plakası </label>
                                            <input
                                                type="text"
                                                name="plateNumber"
                                                value={plateNo}
                                                onChange={(e) => setPlateNo(e.target.value.toUpperCase())}
                                                className="w-full px-4 py-3 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all uppercase"
                                                placeholder="34 ABC 123"
                                                maxLength={9}
                                            />
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Plaka Şehri
                                            </label>
                                            <select
                                                name="plateCity"
                                                value={plateCity}
                                                onChange={(e) => setPlateCity(e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            >
                                                <option value="">Şehir seçin</option>
                                                {cities.map(city => (
                                                    <option key={city} value={city}>{city}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Ruhsat Seri No
                                        </label>
                                        <input
                                            type="text"
                                            name="registrationNo"
                                            value={registrationNo}
                                            onChange={(e) => setRegistrationNo(e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            placeholder="Ruhsat Seri Numarası"
                                            maxLength={8}
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={handleNextStep}
                                    disabled={loading}
                                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-blue-400 transition-colors shadow-lg shadow-blue-200 flex items-center justify-center"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            <span>Araç Bilgileri Alınıyor...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Devam Et</span>
                                            <ArrowRight className="w-5 h-5 ml-2" />
                                        </>
                                    )}
                                </button>
                            </div>
                        )}

                        {/* Adım 2: Kişisel Bilgiler */}
                        {step === 2 && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-center mb-2">
                                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                                        <Shield className="w-5 h-5 text-white" />
                                    </div>
                                    <h2 className="text-2xl font-semibold text-gray-800">
                                        Kişisel Bilgiler
                                    </h2>
                                </div>
                                <p className="text-gray-600 text-center mb-6">
                                    Lütfen kimlik bilgilerinizi giriniz
                                </p>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            TC Kimlik No / Vergi No
                                        </label>
                                        <input
                                            type="text"
                                            name="tcOrTaxNo"
                                            value={tcOrTaxNo}
                                            onChange={(e) => setTcOrTaxNo(e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            placeholder="TC Kimlik No / Vergi No"
                                            maxLength={11}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Doğum Tarihi
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="date"
                                                name="birthDate"
                                                value={birthDate}
                                                onChange={(e) => setBirthDate(e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Telefon Numarası
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="tel"
                                                name="phoneNumber"
                                                value={phoneNumber}
                                                onChange={(e) => setPhoneNumber(e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                placeholder="5XX XXX XX XX"
                                            />
                                            <Phone className="absolute right-3 top-3.5 w-5 h-5 text-gray-400" />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <button
                                        onClick={handlePrevStep}
                                        className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-400 transition-colors flex items-center justify-center"
                                    >
                                        <ArrowLeft className="w-5 h-5 mr-2" />
                                        Geri
                                    </button>
                                    <button
                                        onClick={handleNextStep}
                                        disabled={loading}
                                        className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 flex items-center justify-center"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            </>
                                        ) : (
                                            <>
                                                <span>Devam Et</span>
                                                <ArrowRight className="w-5 h-5 ml-2" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Adım 3: Telefon Doğrulama */}
                        {step === 3 && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-center mb-2">
                                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                                        <Phone className="w-5 h-5 text-white" />
                                    </div>
                                    <h2 className="text-2xl font-semibold text-gray-800">
                                        Telefon Doğrulama
                                    </h2>
                                </div>
                                <p className="text-gray-600 text-center mb-6">
                                    {phoneNumber} numarasına gönderilen 6 haneli kodu giriniz
                                </p>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Doğrulama Kodu
                                    </label>
                                    <input
                                        type="password"
                                        name="verificationCode"
                                        value={verificationCode}
                                        onChange={(e) => setVerificationCode(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-center text-xl tracking-widest"
                                        placeholder="• • • • • •"
                                        maxLength={6}
                                    />
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        onClick={handlePrevStep}
                                        className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-400 transition-colors flex items-center justify-center"
                                    >
                                        <ArrowLeft className="w-5 h-5 mr-2" />
                                        Geri
                                    </button>
                                    <button
                                        onClick={handleNextStep}
                                        disabled={loading}
                                        className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 flex items-center justify-center"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            </>
                                        ) : (
                                            <>
                                                <span>Devam Et</span>
                                                <ArrowRight className="w-5 h-5 ml-2" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Adım 4: Özet ve Teklifler */}
                        {step === 4 && personalInfo && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-center mb-2">
                                    <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center mr-3">
                                        <CheckCircle2 className="w-5 h-5 text-white" />
                                    </div>
                                    <h2 className="text-2xl font-semibold text-gray-800">
                                        Bilgilerinizi Kontrol Edin
                                    </h2>
                                </div>
                                <p className="text-gray-600 text-center mb-6">
                                    Bilgileriniz doğruysa kasko tekliflerini alabilirsiniz
                                </p>

                                {/* Kişisel Bilgiler */}
                                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                                    <div className="flex justify-between items-center mb-3">
                                        <h3 className="font-semibold text-gray-800">Kişisel Bilgiler</h3>
                                        <button
                                            onClick={() => setEditMode(prev => ({ ...prev, personal: !prev.personal }))}
                                            className="flex items-center text-blue-600 hover:text-blue-700 text-sm"
                                        >
                                            <Edit2 className="w-4 h-4 mr-1" />
                                            {editMode.personal ? 'Kaydet' : 'Düzenle'}
                                        </button>
                                    </div>

                                    {editMode.personal ? (
                                        <div className="space-y-3">
                                            <input
                                                type="text"
                                                value={personalInfo.name}
                                                onChange={e => setPersonalInfo({ ...personalInfo, name: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg text-sm"
                                                placeholder="Ad"
                                            />
                                            <input
                                                type="text"
                                                value={personalInfo.surname}
                                                onChange={e => setPersonalInfo({ ...personalInfo, surname: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg text-sm"
                                                placeholder="Soyad"
                                            />
                                            <input
                                                type="text"
                                                value={personalInfo.tcOrTaxNo}
                                                onChange={e => setPersonalInfo({ ...personalInfo, tcOrTaxNo: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg text-sm"
                                                placeholder="TC Kimlik No"
                                            />
                                        </div>
                                    ) : (
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-700">Ad Soyad:</span>
                                                <span className="font-medium text-gray-600">{personalInfo.name} {personalInfo.surname}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-700">TC Kimlik No:</span>
                                                <span className="font-medium text-gray-600">{personalInfo.tcOrTaxNo}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-700">Doğum Tarihi:</span>
                                                <span className="font-medium text-gray-600">{formatDate(personalInfo.birthDate)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Araç Bilgileri */}
                                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                                    <div className="flex justify-between items-center mb-3">
                                        <h3 className="font-semibold text-gray-800">Araç Bilgileri</h3>
                                        <button
                                            onClick={() => setEditMode(prev => ({ ...prev, vehicle: !prev.vehicle }))}
                                            className="flex items-center text-green-600 hover:text-green-700 text-sm"
                                        >
                                            <Edit2 className="w-4 h-4 mr-1" />
                                            {editMode.vehicle ? 'Kaydet' : 'Düzenle'}
                                        </button>
                                    </div>

                                    {editMode.vehicle ? (
                                        <div className="space-y-4">
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Plaka *</label>
                                                    <input
                                                        type="text"
                                                        value={basicInfo.plate}
                                                        onChange={(e) => setBasicInfo(prev => ({ ...prev, plate: e.target.value.toUpperCase() }))}
                                                        className="w-full px-3 py-2 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="34 ABC 123"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Ruhsat Seri No *</label>
                                                    <input
                                                        type="text"
                                                        value={basicInfo.licenseSerialNumber}
                                                        onChange={(e) => setBasicInfo(prev => ({ ...prev, licenseSerialNumber: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="Ruhsat seri numarası"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Araç Tipi</label>
                                                    <input
                                                        type="text"
                                                        value={vehicleTechnicalInfo.vehicleKindName || ''}
                                                        onChange={(e) => setVehicleTechnicalInfo(prev => ({ ...prev, vehicleKindName: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="Binek, Ticari vb."
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Model Yılı</label>
                                                    <input
                                                        type="number"
                                                        value={vehicleTechnicalInfo.modelYear || ''}
                                                        onChange={(e) => setVehicleTechnicalInfo(prev => ({ ...prev, modelYear: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="2024"
                                                        min="1900"
                                                        max="2030"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Marka</label>
                                                    <input
                                                        type="text"
                                                        value={vehicleTechnicalInfo.vehicleBrandName || ''}
                                                        onChange={(e) => setVehicleTechnicalInfo(prev => ({ ...prev, vehicleBrandName: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="Araç markası"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                                                    <input
                                                        type="text"
                                                        value={vehicleTechnicalInfo.vehicleModelName || ''}
                                                        onChange={(e) => setVehicleTechnicalInfo(prev => ({ ...prev, vehicleModelName: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="Araç modeli"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Motor No</label>
                                                    <input
                                                        type="text"
                                                        value={vehicleTechnicalInfo.engineNumber || ''}
                                                        onChange={(e) => setVehicleTechnicalInfo(prev => ({ ...prev, engineNumber: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="Motor numarası"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Şase No</label>
                                                    <input
                                                        type="text"
                                                        value={vehicleTechnicalInfo.chassisNumber || ''}
                                                        onChange={(e) => setVehicleTechnicalInfo(prev => ({ ...prev, chassisNumber: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 placeholder:text-gray-400 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        placeholder="Şase numarası"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Yakıt Türü</label>
                                                    <select
                                                        value={normalizeFuelType(vehicleTechnicalInfo?.fuelType) || ''}
                                                        onChange={(e) => setVehicleTechnicalInfo(prev => ({ ...prev, fuelType: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    >
                                                        <option value="">Seçiniz</option>
                                                        <option value="benzin">Benzin</option>
                                                        <option value="dizel">Dizel</option>
                                                        <option value="elektrik">Elektrik</option>
                                                        <option value="hibrit">Hibrit</option>
                                                        <option value="lpg">LPG</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="space-y-2 text-sm">
                                                <div className="flex justify-between items-start gap-4">
                                                    <span className="text-gray-600 shrink-0">Plaka:</span>
                                                    <span className="font-medium text-gray-900 text-right min-w-0 flex-1">
                                                        {basicInfo.plate || "-"}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-start gap-4">
                                                    <span className="text-gray-600 shrink-0">Ruhsat Seri No:</span>
                                                    <span className="font-medium text-gray-900 text-right min-w-0 flex-1">
                                                        {basicInfo.licenseSerialNumber || "-"}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-start gap-4">
                                                    <span className="text-gray-600 shrink-0">Araç Tipi:</span>
                                                    <span className="font-medium text-gray-900 text-right min-w-0 flex-1">
                                                        {vehicleTechnicalInfo.vehicleKindName || "-"}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-start gap-4">
                                                    <span className="text-gray-600 shrink-0">Model Yılı:</span>
                                                    <span className="font-medium text-gray-900 text-right min-w-0 flex-1">
                                                        {vehicleTechnicalInfo.modelYear || "-"}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-start gap-4">
                                                    <span className="text-gray-600 shrink-0">Marka:</span>
                                                    <span className="font-medium text-gray-900 text-right min-w-0 flex-1">
                                                        {vehicleTechnicalInfo.vehicleBrandName || "-"}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-start gap-4">
                                                    <span className="text-gray-600 shrink-0">Model:</span>
                                                    <span className="font-medium text-gray-900 text-right min-w-0 flex-1">
                                                        {vehicleTechnicalInfo.vehicleModelName || "-"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={handleNextStep}
                                    disabled={apiLoading}
                                    className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 disabled:bg-green-400 transition-colors shadow-lg shadow-green-200 flex items-center justify-center"
                                >
                                    {apiLoading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        </>
                                    ) : (
                                        <>
                                            <span>Teklifleri Al</span>
                                            <ArrowRight className="w-5 h-5 ml-2" />
                                        </>
                                    )}
                                </button>

                                <button
                                    onClick={handlePrevStep}
                                    className="w-full bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-400 transition-colors flex items-center justify-center"
                                >
                                    <ArrowLeft className="w-5 h-5 mr-2" />
                                    Geri Dön
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Dialog
                isVisible={!!customError}
                message={customError}
                type="error"
                duration={5000}
                onClose={() => setCustomError(null)}
            />
        </section>
    );
}